import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react'
import i18n from 'i18next'

import Modal from '../../../UniformComponents/Modal'
import ModalFooter from '../../../UniformComponents/ModalFooter'
import ButtonGroup from '../../../UniformComponents/ButtonGroup'
import Button from '../../../UniformComponents/Button'
import Typography from '../../../UniformComponents/Typography'
import Select from '../../form/Select'
import Input from '../../form/Input'

import './configureLinkModal.scss'
import Campaign from '../models/Campaign'

const getVariableOptions = (survey) => {
    const variableOptions = [
        { value: 'UID', label: i18n.t('distributionConfigureLinkModal.variableUid') },
    ]

    const companyNameQuestion = survey.pages.map(({ questions }) => questions ?? []).flat().find(({ questionId }) => questionId.toUpperCase() === process.env.COMPANY_NAME_PAGEQUESTION_ID)

    if (companyNameQuestion) {
        variableOptions.push({
            value: companyNameQuestion.id,
            label: 'Company name',
        })
    }

    return variableOptions
}

const ConfigureLinkModal = observer(({ distributeStore }) => {
    const [campaign, setCampaign] = useState(new Campaign({}))
    const { store } = distributeStore
    const { survey, useLatestUI } = store

    const variableOptions = getVariableOptions(survey)

    const loadCampaign = async () => {
        try {
            const campaign = await distributeStore.getCampaignNew()

            setCampaign(campaign)
        } catch (e) {
            console.error(e)
            store.handleError({ body: 'Error loading campaign', level: 'error' })
            distributeStore.closeConfigureLinkModal()
        }
    }

    useEffect(() => {
        if (distributeStore.showConfigureLinkModal) {
            loadCampaign()
        }
    }, [distributeStore.showConfigureLinkModal])

    const usedVariables = Object.keys(campaign.tracking)
    const canAddVariable = usedVariables.length < variableOptions.length

    const updateRow = (variable, changes) => {
        const tracking = { ...campaign.tracking }
        tracking[variable] = { ...tracking[variable], ...changes }
        campaign.tracking = tracking
    }

    const changeVariable = (oldVariable, newVariable) => {
        if (oldVariable === newVariable) return
        const tracking = { ...campaign.tracking }
        tracking[newVariable] = tracking[oldVariable]
        Reflect.deleteProperty(tracking, oldVariable)
        campaign.tracking = tracking
    }

    const removeRow = (variable) => {
        const tracking = { ...campaign.tracking }
        Reflect.deleteProperty(tracking, variable)
        campaign.tracking = tracking
    }

    const addRow = () => {
        const nextOption = variableOptions.find((option) => !usedVariables.includes(option.value))
        if (!nextOption) return

        const tracking = { ...campaign.tracking }
        tracking[nextOption.value] = {
            type: 'fixed',
            value: '',
        }

        campaign.tracking = tracking
    }

    const getRowOptions = (variable) => variableOptions.filter(
        (option) => option.value === variable || !usedVariables.includes(option.value),
    )

    const testLink = () => {
        window.open(campaign.url(survey, useLatestUI), '_blank', 'noopener,noreferrer')
    }

    const saveTracking = async () => {
        await distributeStore.saveTracking(campaign)
        distributeStore.closeConfigureLinkModal()
    }

    return (
        <Modal
            show={distributeStore.showConfigureLinkModal}
            onClose={distributeStore.closeConfigureLinkModal}
            cssClass="configure-link-modal"
        >
            <div className="uc-modal__header">
                <Typography type="h2">{i18n.t('distributionConfigureLinkModal.title')}</Typography>
            </div>

            <Typography type="p">{i18n.t('distributionConfigureLinkModal.description')}</Typography>

            <div className="configure-link-banner">
                <i className="fa-light fa-link" />
                <Typography type="p">
                    {i18n.t('distributionConfigureLinkModal.configuring')}
                    {' '}
                    <b>{campaign.title}</b>
                </Typography>
            </div>

            <Typography type="b">{i18n.t('distributionConfigureLinkModal.identifyingVariables')}</Typography>

            <div className="configure-link-variables">
                {usedVariables.length > 0 && (
                    <div className="configure-link-variables-header">
                        <Typography type="tiny">{i18n.t('distributionConfigureLinkModal.variable')}</Typography>
                        <Typography type="tiny">{i18n.t('distributionConfigureLinkModal.type')}</Typography>
                        <Typography type="tiny">{i18n.t('distributionConfigureLinkModal.property')}</Typography>
                        <span />
                    </div>
                )}

                {Object.entries(campaign.tracking)
                    .filter(([variable]) => !variableOptions.find((option) => option.value === variable)?.hidden)
                    .map(([variable, { type, value }]) => (
                        <div className="configure-link-variable-row" key={variable}>
                            <Select
                                name={`configure-link-variable-${variable}`}
                                options={getRowOptions(variable)}
                                value={variable}
                                onBlur={({ value }) => changeVariable(variable, value)}
                            />

                            <ButtonGroup cssClass="configure-link-type-toggle">
                                <Button
                                    cssClass={`configure-link-type-btn ${type === 'merge' ? 'active' : ''}`}
                                    label={i18n.t('distributionConfigureLinkModal.mergeField')}
                                    onClick={() => updateRow(variable, { type: 'merge' })}
                                />
                                <Button
                                    cssClass={`configure-link-type-btn ${type === 'fixed' ? 'active' : ''}`}
                                    label={i18n.t('distributionConfigureLinkModal.fixedValue')}
                                    onClick={() => updateRow(variable, { type: 'fixed' })}
                                />
                            </ButtonGroup>

                            <div className="configure-link-property">
                                <Input
                                    name={`configure-link-property-${variable}`}
                                    value={value}
                                    placeholder={type === 'merge'
                                        ? i18n.t('distributionConfigureLinkModal.mergeFieldPlaceholder')
                                        : i18n.t('distributionConfigureLinkModal.fixedValuePlaceholder')}
                                    onChange={({ value }) => updateRow(variable, { value })}
                                />
                                <Typography type="tiny" cssClass="configure-link-property-hint">
                                    {type === 'merge'
                                        ? i18n.t('distributionConfigureLinkModal.mergeFieldHint')
                                        : i18n.t('distributionConfigureLinkModal.fixedValueHint')}
                                </Typography>
                            </div>

                            <i
                                className="fal fa-trash-alt configure-link-remove-row"
                                onClick={() => removeRow(variable)}
                            />
                        </div>
                    ))}

                {canAddVariable && (
                    <Button
                        cssClass="secondary link-btn"
                        icon="fal fa-plus"
                        label={i18n.t('distributionConfigureLinkModal.addVariable')}
                        onClick={addRow}
                    />
                )}

                <Typography type="tiny" cssClass="configure-link-footnote">
                    {i18n.t('distributionConfigureLinkModal.variablesFootnote')}
                </Typography>
            </div>

            <Typography type="b">{i18n.t('distributionConfigureLinkModal.previewTitle')}</Typography>

            <div className="configure-link-preview">
                <Typography type="tiny">{campaign.url(survey, useLatestUI)}</Typography>
            </div>

            <Typography type="tiny" cssClass="configure-link-footnote">
                {i18n.t('distributionConfigureLinkModal.previewFootnote')}
            </Typography>

            <ModalFooter cssClass="configure-link-modal-buttons">
                <Button
                    cssClass="regular"
                    variant="tertiary"
                    onClick={distributeStore.closeConfigureLinkModal}
                >
                    {i18n.t('distributionConfigureLinkModal.actionCancel')}
                </Button>

                <div className="configure-link-modal-buttons-right">
                    <Button
                        cssClass="regular"
                        icon="fal fa-flask"
                        variant="secondary"
                        onClick={testLink}
                    >
                        {i18n.t('distributionConfigureLinkModal.actionTestLink')}
                    </Button>

                    <Button
                        cssClass="regular"
                        onClick={saveTracking}
                        variant="primary"
                    >
                        {i18n.t('distributionConfigureLinkModal.actionSaveTracking')}
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    )
})

export default ConfigureLinkModal
